<script setup lang="ts">
import KartNavigation from "../../../components/website/KartNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { breakpointsSematic, useBreakpoints } from "@vueuse/core";
import { useOwner } from "../../../stores/website/owner";
import HappyKartDayPath from "../../../assets/website/document/invitation-happy-kart-day.pdf";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const breakpoints = useBreakpoints(breakpointsSematic);
const isPhone = breakpoints.smaller("tablet");

const { setTranslateNamespace } = useTranslate();
setTranslateNamespace(".kart.birthday");

const ownerManager = useOwner();

function downloadInvitation() {
    window.open(
        `${window.location.protocol}//${window.location.host}/${HappyKartDayPath}`,
    );
}

function removePrefixPhoneNumber(phoneNumber: string) {
    return phoneNumber.replace(/^\+[0-9]+\s*/, "0");
}

useSeoMeta({
    title: "Anniversaire karting enfant | Happy Kart Day dès 7 ans",
    description:
        "Organisez un anniversaire karting inoubliable pour enfants de 7 à 13 ans. Formule Happy Kart Day : runs chronométrés, gaufres, boissons et espace privatisé. Anniversaire Clermont-Ferrand.",
    path: "/kart/birthday",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Faire du kart", path: "/kart" },
        { name: "Anniversaire", path: "/kart/birthday" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Event",
            name: "Happy Kart Day - Anniversaire karting",
            description:
                "Anniversaire karting pour enfants de 7 à 13 ans avec runs, gaufres et boissons.",
            url: "https://speed2max.com/kart/birthday",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            organizer: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "À partir de quel âge peut-on fêter son anniversaire chez Speed2Max ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Les formules Happy Kart Day sont accessibles aux enfants de 7 à 13 ans.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Que comprend la formule anniversaire karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule Happy Kart Day comprend des runs de karting chronométrés, des gaufres, des boissons et un espace privatisé pour le goûter.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Combien d'enfants minimum pour un anniversaire karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Les formules anniversaire sont disponibles à partir de 5 enfants. Contactez-nous pour les grands groupes.",
                    },
                },
            ],
        },
    ],
});
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <KartNavigation current="birthday"></KartNavigation>
        <ImageHeader secondary>
            <template #image>
                <img
                    src="/images/kart/faireDuKart/FDK-birthday-header.jpg"
                    alt="Anniversaire karting enfant Speed2Max" />
            </template>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #subtitle>
                <span class="has-text-white"
                    ><Translate path=".subtitle1"></Translate></span
                >&nbsp; <span><Translate path=".subtitle2"></Translate></span>&nbsp;
                <span class="has-text-white"
                    ><Translate path=".subtitle3"></Translate
                ></span>
            </template>
            <template #icon>
                <img
                    src="/images/kart/icon-faire-du-kart.svg"
                    alt="Icône karting électrique" />
            </template>
        </ImageHeader>
        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content>
                <Translate path=".content"></Translate>
            </template>
            <template #moreInfos>
                <Translate path=".moreInfos"></Translate>
            </template>
            <template #icon>
                <img
                    src="/images/kart/who-birthday.png"
                    alt="Enfants célébrant un anniversaire au karting" />
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>

        <VFlex
            :flex-direction="isPhone ? 'column' : 'row'"
            justify-content="center"
            row-gap="2rem"
            column-gap="2rem"
            class="px-6 py-5">
            <TranslateNamespace path=".pack.gold">
                <VFlex
                    flex-direction="column"
                    class="p-5 has-border-gold pack-block"
                    align-items="center">
                    <img
                        src="/images/kart/top-left-gold.png"
                        class="top-left-img"
                        alt="" />
                    <img
                        src="/images/kart/gold/pack.png"
                        alt="Pack anniversaire karting"
                        class="m-5"
                        style="height: 100px" />
                    <VDivider color="gold" class="is-align-self-stretch"></VDivider>

                    <VFlex
                        flex-direction="column"
                        class="is-flex is-align-self-stretch py-1"
                        align-items="start">
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-gold">•&nbsp;</span>
                            <Translate path=".feature1"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-gold">•&nbsp;</span>
                            <Translate path=".feature2"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-gold">•&nbsp;</span>
                            <Translate path=".feature3"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-gold">•&nbsp;</span>
                            <Translate path=".feature4"></Translate>
                        </p>
                    </VFlex>

                    <VDivider color="gold" class="is-align-self-stretch"></VDivider>
                    <p class="is-size-5 has-text-weight-bold has-text-gold is-uppercase">
                        <span v-if="ownerManager.owner?.website?.priceBirthdayPackGold">
                            <PriceView
                                :price="ownerManager.owner.website?.priceBirthdayPackGold"
                                :owner="ownerManager.owner"
                                class="has-text-weight-bold"
                                :with-currency="true"></PriceView>
                            <Translate path=".price"></Translate>
                        </span>
                        <Translate v-else>.noPrice</Translate>
                    </p>
                    <VFlex
                        flex-direction="column"
                        align-items="center"
                        class="has-gradient-background-dark-ltr is-align-self-stretch gift-block">
                        <img
                            src="/images/kart/gift.png"
                            alt="Cadeau inclus dans la formule anniversaire" />
                        <p
                            class="has-text-white is-size-5 is-uppercase has-text-weight-bold">
                            <span class="has-text-primary">
                                <Translate path=".gift"></Translate>
                            </span>
                            <Translate path=".gift2"></Translate>
                        </p>
                    </VFlex>
                </VFlex>
            </TranslateNamespace>
            <TranslateNamespace path=".pack.silver">
                <VFlex
                    flex-direction="column"
                    class="p-5 has-border-silver pack-block"
                    align-items="center">
                    <img
                        src="/images/kart/top-left-silver.png"
                        class="top-left-img"
                        alt="" />
                    <img
                        src="/images/kart/silver/pack.png"
                        alt="Pack anniversaire karting"
                        class="m-5"
                        style="height: 100px" />
                    <VDivider color="silver" class="is-align-self-stretch"></VDivider>

                    <VFlex
                        flex-direction="column"
                        class="is-flex is-align-self-stretch py-1"
                        align-items="start">
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-silver">•&nbsp;</span>
                            <Translate path=".feature1"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-silver">•&nbsp;</span>
                            <Translate path=".feature2"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-silver">•&nbsp;</span>
                            <Translate path=".feature3"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-silver">•&nbsp;</span>
                            <Translate path=".feature4"></Translate>
                        </p>
                    </VFlex>

                    <VDivider color="silver" class="is-align-self-stretch"></VDivider>
                    <p
                        class="is-size-5 has-text-weight-bold has-text-silver is-uppercase">
                        <span v-if="ownerManager.owner?.website?.priceBirthdayPackSilver">
                            <PriceView
                                class="has-text-weight-bold"
                                :price="
                                    ownerManager.owner.website?.priceBirthdayPackSilver
                                "
                                :owner="ownerManager.owner"
                                :with-currency="true"></PriceView>
                            <Translate path=".price"></Translate>
                        </span>
                        <Translate v-else>.noPrice</Translate>
                    </p>
                    <VFlex
                        flex-direction="column"
                        align-items="center"
                        class="has-gradient-background-dark-ltr is-align-self-stretch gift-block">
                        <img
                            src="/images/kart/gift.png"
                            alt="Cadeau inclus dans la formule anniversaire" />
                        <p
                            class="has-text-white is-size-5 is-uppercase has-text-weight-bold">
                            <span class="has-text-primary">
                                <Translate path=".gift"></Translate>
                            </span>
                            <Translate path=".gift2"></Translate>
                        </p>
                    </VFlex>
                </VFlex>
            </TranslateNamespace>
            <TranslateNamespace path=".pack.bronze">
                <VFlex
                    flex-direction="column"
                    class="p-5 has-border-bronze pack-block"
                    align-items="center">
                    <img
                        src="/images/kart/top-left-bronze.png"
                        class="top-left-img"
                        alt="" />
                    <img
                        src="/images/kart/bronze/pack.png"
                        alt="Pack anniversaire karting"
                        class="m-5"
                        style="height: 100px" />
                    <VDivider color="bronze" class="is-align-self-stretch"></VDivider>

                    <VFlex
                        flex-direction="column"
                        class="is-flex is-align-self-stretch py-1"
                        align-items="start">
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-bronze">•&nbsp;</span>
                            <Translate path=".feature1"></Translate>
                        </p>
                        <p class="is-size-5 has-text-white is-uppercase my-2">
                            <span class="has-text-bronze">•&nbsp;</span>
                            <Translate path=".feature2"></Translate>
                        </p>
                    </VFlex>

                    <VDivider color="bronze" class="is-align-self-stretch"></VDivider>
                    <p
                        class="is-size-5 has-text-weight-bold has-text-bronze is-uppercase">
                        <span v-if="ownerManager.owner?.website?.priceBirthdayPackBronze">
                            <PriceView
                                class="has-text-weight-bold"
                                :price="
                                    ownerManager.owner.website?.priceBirthdayPackBronze
                                "
                                :owner="ownerManager.owner"
                                :with-currency="true"></PriceView>
                            <Translate path=".price"></Translate>
                        </span>
                        <Translate v-else>.noPrice</Translate>
                    </p>
                    <VFlex
                        flex-direction="column"
                        align-items="center"
                        class="is-align-self-stretch gift-block">
                    </VFlex>
                </VFlex>
            </TranslateNamespace>
        </VFlex>

        <VFlex flex-direction="column" class="px-6 py-5">
            <TranslateNamespace path=".supplement">
                <VFlex flex-direction="column" class="has-background-green p-5">
                    <p
                        class="title-session-plus has-text-white has-text-weight-bold is-uppercase">
                        <Translate path=".contentP1"></Translate>
                    </p>
                    <p class="subtitle-session-plus has-text-white">
                        <Translate path=".contentP2"></Translate>
                    </p>
                </VFlex>
            </TranslateNamespace>
        </VFlex>

        <VFlex
            :flex-direction="isPhone ? 'column' : 'row'"
            class="has-gradient-background-dark-rtl mx-6 my-5 block-invit"
            column-gap="1em"
            justify-content="space-between">
            <VFlex
                flex-direction="column"
                class="p-5 is-flex-grow-1 is-flex-shrink-1 w-100"
                :align-items="isPhone ? 'center' : 'start'">
                <p class="is-uppercase is-size-4 has-text-weight-bold has-text-white">
                    <span class="has-text-primary">
                        <Translate path=".invit.titleP1"></Translate>
                    </span>
                    <Translate path=".invit.titleP2"></Translate>
                    <span class="has-text-primary">
                        <Translate path=".invit.titleP3"></Translate>
                    </span>
                </p>
                <VButton
                    color="info"
                    size="huge"
                    class="my-5"
                    @click="downloadInvitation()">
                    <Translate path=".invit.button"></Translate>
                </VButton>
                <p class="is-size-6 has-text-white has-text-weight-semibold">
                    <Translate path=".invit.content"></Translate>
                </p>
            </VFlex>
            <VFlex
                flex-direction="row"
                justify-content="end"
                class="is-flex-grow-1 is-flex-shrink-1 img-invit">
                <!-- <img
                    src="/images/kart/invit.png"
                    alt="invit"/> -->
            </VFlex>
        </VFlex>
        <VFlex class="px-6 py-5 mb-5" flex-direction="column">
            <VFlex v-if="ownerManager.ownerID === null" :flex-direction="'column'">
                <p class="is-size-6 mb-4">
                    <Translate path=".infoChooseOwner"></Translate>
                </p>
                <ButtonPrimary
                    color="primary"
                    class="is-align-self-flex-start mt-none is-mohol is-size-6"
                    style="font-family: mohol !important"
                    @click="$router.push('/owners')">
                    <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                    <span class="is-uppercase">
                        <Translate path=".selectOwner"></Translate>
                    </span>
                </ButtonPrimary>
            </VFlex>
            <Banner class="m-5 has-gradient-background-dark-ltr w-100 is-weight-600">
                <Translate path=".banner"></Translate><br />
                <a
                    v-if="ownerManager.owner"
                    class="is-weight-900"
                    :href="`tel:${ownerManager.owner?.phoneNumber}`"
                    >{{ removePrefixPhoneNumber(ownerManager.owner?.phoneNumber) }}</a
                >
            </Banner>
        </VFlex>

        <Footer></Footer>
    </VFlex>
</template>

<style scoped lang="scss">
.w-100 {
    width: 100%;
}

.block-invit {
    border-radius: 5px;
}

.img-invit {
    width: 100%;
    background-image: url("/images/kart/invit.jpg");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right;
}

.has-background-green {
    border-radius: 5px;
    background-image: linear-gradient(to bottom, rgb(255 255 255 / 0%) 0%, $primary 100%),
        url("/images/kart/background-supplement.png");
    background-size: cover;
}

.pack-block {
    position: relative;
    border-radius: 5px;
    width: 100%;

    .top-left-img {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
    }
}

.gift-block {
    position: relative;
    margin-top: 70px;
    padding-top: 70px;
    margin-left: -20px;
    margin-right: -20px;
    margin-bottom: -20px;
    height: 110px;

    > img {
        top: -50%;
        position: absolute;
        z-index: 0;
    }
}

.title-session-plus,
.subtitle-session-plus {
    font-size: 2rem;
}

@media (max-width: 767px) {
    .title-session-plus,
    .subtitle-session-plus {
        font-size: 1.5rem;
    }
}

.banner {
    a {
        &:hover {
            color: white;
        }
    }
}
</style>
